import React, { useContext, useMemo } from 'react'

import type { BarChartSlotComponentProps, BarElementOwnerState, BarElementProps } from '@mui/x-charts'
import { BarChart, BarElement } from '@mui/x-charts/BarChart'
import classnames from 'classnames'

import filterContext from '@/components/pages/production/components/context'
import ListingsContext from '@/context/listings'

import { ACTIVE_COLOR, BAR_COUNT, CHART_PROPS, INACTIVE_COLOR } from '../price-slider/constants'
import { useLowestAndHighestListingsPrice } from '../price-slider/hook'

import styles from './styles.module.scss'

export type TSliderValue = [number, number]
type TBarData = Record<number, number>

type BarCustomElementProps = {
    sliderRange: TSliderValue
    lowestPrice: number
    priceStep: number
    ownerState: BarElementOwnerState
}
export const BarCustomElement: React.JSXElementConstructor<BarElementProps & BarCustomElementProps> = (props) => {
    const { sliderRange, lowestPrice, priceStep, ownerState, ...others } = props
    const currentBaseValue = lowestPrice + priceStep * ownerState.dataIndex
    const currentHighValue = Math.floor(currentBaseValue + priceStep)
    const isWithinRange = currentBaseValue >= sliderRange[0] && currentHighValue <= sliderRange[1]

    return (
        <BarElement
            {...others}
            dataIndex={ownerState.dataIndex}
            id={`price-histogram-bar-${ownerState.dataIndex}`}
            data-testid={`price-histogram-bar-${ownerState.dataIndex}`}
            rx={2}
            style={{
                ...props.style,
                fill: isWithinRange ? ACTIVE_COLOR : INACTIVE_COLOR,
            }}
        />
    )
}

type PriceHistogramProps = {
    displayMinMaxVal: [number | '', number | '']
    className: string
}
export const PriceHistogram: React.FC<PriceHistogramProps> = ({ displayMinMaxVal, className }) => {
    const {
        state: { showAllInPrice },
    } = useContext(filterContext)
    const {
        selectors: { composedListings, globalPriceRangeObject: priceRangeObj },
    } = useContext(ListingsContext)

    const [lowestListingsPrice, highestListingsPrice] = useLowestAndHighestListingsPrice()

    const priceStep = (highestListingsPrice - lowestListingsPrice) / BAR_COUNT
    const priceHistogramData = useMemo(() => {
        const allListingsPrices = composedListings.map((listing: any) => {
            const price = showAllInPrice ? listing?.allInPrice : listing?.price
            // Higheset price may not be the "highest price" for filter, since it can be either
            // 1. from `globalPriceObject`, or 2. Median Price * 4
            // In scenario #2, we catagorize them into the highest price in the Histogram
            return price <= highestListingsPrice ? price : highestListingsPrice
        })
        const emptyBarDataObj: TBarData = {}
        for (let i = 0; i < BAR_COUNT; i++) {
            emptyBarDataObj[i] = 0
        }

        const barData: TBarData = allListingsPrices.reduce((accu: TBarData, currPrice: number) => {
            const ceilCurrPrice = Math.ceil(currPrice)
            const bucketIndex =
                ceilCurrPrice === highestListingsPrice
                    ? BAR_COUNT - 1 // enclose highest price in the last bar
                    : Math.floor((ceilCurrPrice - lowestListingsPrice) / priceStep)
            return { ...accu, [bucketIndex]: (accu[bucketIndex] || 0) + 1 }
        }, emptyBarDataObj)

        const data = Object.entries(barData).map(([, value]) => value || 0)

        return data
    }, [priceRangeObj, showAllInPrice, priceStep])

    const shouldHideHistogram = highestListingsPrice - lowestListingsPrice <= BAR_COUNT
    if (shouldHideHistogram) return <></>

    return (
        <div className={classnames(className, styles.priceHistogram)} data-testid="price-histogram">
            <BarChart
                {...CHART_PROPS}
                series={[{ data: priceHistogramData }]}
                slots={{ bar: BarCustomElement as React.JSXElementConstructor<BarElementProps> }}
                slotProps={
                    {
                        bar: {
                            sliderRange: displayMinMaxVal,
                            lowestPrice: lowestListingsPrice,
                            priceStep,
                        },
                    } as BarChartSlotComponentProps
                }
            />
        </div>
    )
}

export default PriceHistogram
